import { BLOCKS, MARKS } from '@contentful/rich-text-types';
import {
  FC,
  useEffect,
  useState,
} from 'react';
import cn from 'classnames';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import { useSelector } from 'react-redux';

import type { Store } from 'src/redux/types/storeTypes';
import { Block } from 'src/__generated__/graphqlTypes';
import { CircleImageDescriptionButtonsInterface } from 'types/componentTypes';
import {
  BlogResponse,
  RichTextParsersConfig,
  ZodiacSign,
} from 'types/objectTypes';
import styles from 'components/Sections/RelatedBlogPosts/RelatedBlogPosts.module.scss';
import classes from 'src/styles/_commonClasses.module.scss';
import { RelatedBlogPost, RelatedBlogPostsAPI } from 'components/Sections/RelatedBlogPosts';
import { CommonRichText as Content } from 'components/Shared/SharedComponents';
import Divider from 'components/Sections/Divider';
import {
  CommonSize,
  ElementAlign,
  Heading,
} from 'constants/enums';
import { capitalizeFirstLetter } from 'lib/text.service';
import { mapBlocksHeadingWithReplacement } from 'lib/richTextMappers';
import { getSubCategoriesBlog } from 'src/api/customerApi';
import { Logger } from 'lib/logger';
import LocalLoader from 'src/shared/ui/LocalLoader/LocalLoader';

import type { IRelatedBlogPostArticles } from './declarations';

const getParsersConfig = (
  titleAlign: ElementAlign,
  zodiacSign: ZodiacSign,
): RichTextParsersConfig => {
  const getHeadingClasses = (headingClass: string) => cn(
    styles.blogTitle,
    classes[`titleAlign${capitalizeFirstLetter(titleAlign)}`],
    headingClass,
  );
  const replacementObject = { sign: capitalizeFirstLetter(zodiacSign) };

  return ({
    [BLOCKS.HEADING_1]: {
      classNames: getHeadingClasses(classes.titleMainH1),
      mapper: mapBlocksHeadingWithReplacement(replacementObject, Heading.FIRST),
    },
    [BLOCKS.HEADING_2]: {
      classNames: getHeadingClasses(classes.titleMainH2),
      mapper: mapBlocksHeadingWithReplacement(replacementObject),
    },
    [BLOCKS.HEADING_3]: {
      classNames: getHeadingClasses(classes.titleMainH3),
      mapper: mapBlocksHeadingWithReplacement(replacementObject, Heading.THIRD),
    },
    [BLOCKS.PARAGRAPH]: {
      classNames: styles.blogParagraph,
    },
    [MARKS.BOLD]: {
      classNames: classes.textBold,
    },
  });
};

/* Nested component */
const Title = Content;

/* Nested component */
const Articles: FC<IRelatedBlogPostArticles> = ({ isLoading, blogData }) => {
  const clientWidth = useSelector((store: Store) => store.client.app.clientWidth);
  const { commonTabletMaxWidth } = useSelector((store: Store) => store.server.app.scssVariables);

  const shouldUseCarousel = +commonTabletMaxWidth >= clientWidth;

  if (isLoading) {
    return <LocalLoader withBackground={false} className={styles.loader} />;
  }

  if (shouldUseCarousel) {
    return (
      <Carousel
        swipeable
        showArrows={false}
        className={styles.blogCarousel}
        showStatus={false}
        infiniteLoop
      >
        {blogData}
      </Carousel>
    );
  }

  return (
    <>
      {blogData}
    </>
  );
};

/* Main component */
const RelatedBlogPosts: FC<CircleImageDescriptionButtonsInterface> = ({
  bgColor,
  extraData,
  content: block,
  zodiacSign,
  topDivider,
  bottomDivider,
  commonPageMaxWidth,
}) => {
  const [blogData, setblogData] = useState<Array<BlogResponse>>([]);
  const [showBlogData, setshowBlogData] = useState<Boolean>(false);
  const [isLoading, setLoadingState] = useState<boolean | null>(null);

  const {
    titleAlign = ElementAlign.CENTER,
    verticalPadding = CommonSize.MEDIUM,
  } = extraData || { };
  const {
    contentTypesCollection,
    content,
    richTitle,
    title,
    link,
  } = block || {};

  useEffect(() => {
    (async () => {
      try {
        setLoadingState(true);
        const blogType = title;
        const resp = await getSubCategoriesBlog(blogType || '');
        setblogData(resp);
        setshowBlogData(true);
      } catch (e) {
        Logger.error(e);
      } finally {
        setLoadingState(false);
      }
    })();
  }, []);

  if (!block) {
    return null;
  }

  const circleImages = contentTypesCollection?.items as Array<Block> || [];

  const getBlogData = () => {
    if (blogData.length > 0) {
      return blogData.map((block) => (
        <RelatedBlogPostsAPI
          key={block.id}
          entryName={block.id}
          content={block.textContent}
          richTitle={block.heading}
          link={block.linkURL}
          image={block.imageURL}
          readLink={link!}
        />
      ));
    }

    if (showBlogData) {
      return circleImages.map((block) => (
        <RelatedBlogPost
          key={block.entryName}
          block={block}
        />
      ));
    }
  };

  return (
    <section
      className={cn(
        styles.wrapper,
        classes[`sharedWrapperPaddingVertical${capitalizeFirstLetter(verticalPadding)}`],
      )}
      style={{ background: bgColor }}
    >
      <div className={cn(
        styles.blog,
        { [styles.blogLoaded]: isLoading === false },
      )}
      >
        <Divider
          block={topDivider}
          className={styles.blogDividerTop}
          maxWidth={`${commonPageMaxWidth}px`}
        />
        <Title
          content={richTitle}
          parsersConfig={getParsersConfig(titleAlign, zodiacSign)}
        />
        <hr className={styles.blogUnderline} />
        <Content
          content={content}
          parsersConfig={getParsersConfig(titleAlign, zodiacSign)}
        />
        <Articles
          isLoading={isLoading}
          blogData={getBlogData()}
        />
        <Divider
          block={bottomDivider}
          className={styles.blogDividerBottom}
          maxWidth={`${commonPageMaxWidth}px`}
        />
      </div>
    </section>
  );
};

export default RelatedBlogPosts;
